/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Vers.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren } from 'preact';
import { usePress } from '../hooks/UNSAFE_usePress';
import { useHover } from '../hooks/UNSAFE_useHover';
import { useActive } from '../hooks/UNSAFE_useActive';
import { classNames } from '../utils/UNSAFE_classNames';
import { forwardRef, ForwardedRef } from 'preact/compat';
import { cssProps } from '@oracle/oraclejet-cssprops';
import type { DimensionProps } from '../utils/UNSAFE_interpolations/dimensions';
import { dimensionInterpolations } from '../utils/UNSAFE_interpolations/dimensions';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';
import { getClientHints } from '../utils/PRIVATE_clientHints';
import { useTabbableMode } from '../hooks/UNSAFE_useTabbableMode';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { HTMLAttributesSignalExcluded } from '../utils/UNSAFE_attributeUtils';

export type ActionDetail = {
  reason: 'pointer' | 'keyboard';
};

type BaseButtonElementDetails =
  | { type: 'button' }
  | { type: 'a'; href: string; target?: string; rel?: string }
  | { type: 'span' };

type IntrinsicProps = Pick<
  HTMLAttributesSignalExcluded<HTMLElement>,
  | 'autofocus'
  | 'title'
  | 'onBlur'
  | 'onFocus'
  | 'onMouseEnter'
  | 'onMouseLeave'
  | 'onTouchEnd'
  | 'onTouchStart'
>;
export type StylingOptions =
  | 'default'
  | 'min'
  | 'embedded'
  | 'container'
  | 'unstyled'
  | 'fill'
  | 'noBorderRadiusEnd'
  | 'noBorderRadiusStart'
  | 'active';

type AriaProps = {
  'aria-hidden'?: boolean;
  'aria-expanded'?: boolean;
  'aria-roledescription'?: string;
  'aria-describedby'?: string;
  'aria-haspopup'?: string;
};

export type Props = IntrinsicProps &
  AriaProps &
  DimensionProps & {
    /**
     *The default slot is the button's text label. The oj-c-base-button element accepts plain text or DOM nodes as children for the default slot."
     */
    children?: ComponentChildren;

    /**
     * Specifies that the button element should be disabled.
     */
    isDisabled?: boolean;

    /**
     * Specifies if repeated events are dispatched for key or pointer held down
     */
    isRepeat?: boolean;

    /**
     *
     * Indicates in what states the button has chrome (background and border).
     *     "ghost": "Ghost buttons are the least prominent variation.  Ghost buttons are
     * useful for performing low-priority tasks, such as manipulating the UI.",
     *     "borderless"': "Borderless buttons are a more prominent variation. Borderless
     * buttons are useful for supplemental actions that require minimal emphasis.",
     *     "outlined"': "Outlined buttons are salient, but lighter weight than
     * solid buttons. Outlined buttons are useful for secondary actions.",
     *     "solid"': "Solid buttons stand out, and direct the user's attention to the
     *  most important actions in the UI.",
     *     "callToAction"': "A Call To Action (CTA) button guides the user to take or
     * complete the action that is the main goal of the page or page section. There
     * should only be one CTA button on a page at any given time.",
     *     "danger"': "A Danger button alerts the user to a dangerous situation."
     */
    variant?: 'ghost' | 'borderless' | 'outlined' | 'solid' | 'callToAction' | 'danger';

    /**
     * The size indicates how large the button is rendered: 'xs', "sm", "md" (default), "lg".
     */
    size?: 'xs' | 'sm' | 'md' | 'lg';

    /**
     * Edge can be used to specify that the button is intended to go at the bottom of
     * a region, stretching from left to right: "bottom" or "none" (default).
     */
    edge?: 'none' | 'bottom';

    /**
     * The aria-label is used to provide guidance for screen readers.
     */
    'aria-label'?: string;

    /**
     * Styling can be used for customizing buttons by removing label padding ("min") or
     * removing all styling ("unstyled").
     */
    styling?: StylingOptions[];

    /**
     * Triggered when a button is clicked, whether by keyboard, mouse, or touch events.
     */
    onAction?: (details: ActionDetail) => void;

    /**
     * Indicate element type and associated details
     */
    elementDetails?: BaseButtonElementDetails;
  };

const activeStyles = {
  backgroundImage:
    'linear-gradient(var(--oj-c-PRIVATE-DO-NOT-USE-base-button-bg-overlay-active), var(--oj-c-PRIVATE-DO-NOT-USE-base-button-bg-overlay-active))',
  borderColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-base-button-border-color-active)',
  color: 'var(--oj-c-PRIVATE-DO-NOT-USE-base-button-text-color-active)'
};

const hoverStyles = {
  backgroundImage:
    'linear-gradient(var(--oj-c-PRIVATE-DO-NOT-USE-base-button-bg-overlay-hover),var(--oj-c-PRIVATE-DO-NOT-USE-base-button-bg-overlay-hover))',
  borderColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-base-button-border-color-hover)',
  color: 'var(--oj-c-PRIVATE-DO-NOT-USE-base-button-text-color-hover)'
};

const compStyles = {
  base: cssProps`${{
    alignItems: 'center',
    backgroundColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-base-button-bg-color)',
    borderColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-base-button-border-color)',
    borderRadius: 'var(--oj-c-PRIVATE-DO-NOT-USE-base-button-border-radius)',
    borderStyle: 'solid',
    borderWidth: '1px',
    boxSizing: 'border-box',
    color: 'var(--oj-c-PRIVATE-DO-NOT-USE-base-button-text-color)',
    cursor: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-cursor-clickable)',
    display: 'inline-block',
    fontFamily: 'inherit',
    fontSize: 'var(--oj-c-PRIVATE-DO-NOT-USE-base-button-font-size)',
    fontWeight: 'var(--oj-c-PRIVATE-DO-NOT-USE-base-button-font-weight)',
    height: 'var(--oj-c-PRIVATE-DO-NOT-USE-base-button-height)',
    justifyContent: 'center',
    minWidth: 'var(--oj-c-PRIVATE-DO-NOT-USE-base-button-height)',
    overflowX: 'hidden',
    overflowY: 'hidden',
    paddingInlineStart: 'var(--oj-c-PRIVATE-DO-NOT-USE-base-button-text-to-edge-padding)',
    paddingInlineEnd: 'var(--oj-c-PRIVATE-DO-NOT-USE-base-button-text-to-edge-padding)',
    paddingTop: 0,
    paddingBottom: 0,
    pointerEvents: 'auto',
    textOverflow: 'ellipsis',
    verticalAlign: 'top',
    WebkitTapHighlightColor: 'rgba(0,0,0,0)',
    whiteSpace: 'nowrap',

    // @ts-ignore
    '&:active': activeStyles,

    '&:focus-visible': {
      outlineStyle: 'dotted',
      outlineOffset: '1px',
      outlineWidth: '1px',
      outlineColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-focus-border-color)'
    }
  }}`,

  hover: cssProps`${{
    // @ts-ignore
    '&:not(:active)': hoverStyles
  }}`,

  active: cssProps`${activeStyles}`,

  pseudohover: cssProps`${{
    // @ts-ignore
    '@media(hover: hover)': {
      '&:hover:enabled:not(:active)': hoverStyles
    }
  }}`,

  pseudohoverSpan: cssProps`${{
    // @ts-ignore
    '@media(hover: hover)': {
      '&:hover:not(:active)': hoverStyles
    }
  }}`,

  unstyled: cssProps`${{
    boxSizing: 'border-box',
    cursor: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-cursor-clickable)',
    height: 'var(--oj-c-PRIVATE-DO-NOT-USE-base-button-height)'
  }}`,

  min: cssProps`${{
    paddingInlineStart: '0',
    paddingInlineEnd: '0'
  }}`,

  fill: cssProps`${{
    width: '100%'
  }}`,

  container: cssProps`
   ${{
     backgroundColor: 'transparent',
     borderStyle: 'hidden',
     cursor: 'none',
     display: 'flex',
     '&:active': {
       backgroundColor: 'transparent',
       borderColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-base-button-border-color)'
     }
   }}
 `,

  embedded: cssProps`
   ${{
     height: '100%',
     display: 'flex',
     '&:focus-visible': {
       outlineStyle: 'none'
     }
   }}
 `,

  noBorderRadiusStart: cssProps`
 ${{
   borderStartStartRadius: 0,
   borderEndStartRadius: 0,
   borderInlineStartWidth: '0px'
 }}
 `,

  noBorderRadiusEnd: cssProps`
 ${{
   borderEndEndRadius: 0,
   borderStartEndRadius: 0,
   borderInlineEndWidth: '0px'
 }}
 `,

  disabled: cssProps`${{
    pointerEvents: 'none',
    backgroundColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-base-button-bg-color-disabled)',
    borderColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-base-button-border-color-disabled)',
    color: 'var(--oj-c-PRIVATE-DO-NOT-USE-base-button-text-color-disabled)'
  }}`,

  bottom: cssProps`${{
    borderLeftWidth: '0',
    borderRightWidth: '0',
    borderBlockEndWidth: '0',
    width: '100%',
    borderTopLeftRadius: '0',
    borderTopRightRadius: '0'
  }}`
};

const childrenStyles = cssProps`${{
  /* @ts-ignore */
  '& > *': {
    marginTop: '0 !important',
    marginBottom: '0 !important',
    marginLeft: '0 !important',
    marginRight: '0 !important'
  }
}}`;

const interpolations = [...Object.values(dimensionInterpolations)];

const styleInterpolations = mergeInterpolations<Props>(interpolations);

const clientHints = getClientHints();
// :active only works on IOS devices if a touch handler exists
const iosProps = clientHints.platform === 'ios' ? { ontouchstart: function () {} } : {};
const isHybrid = getClientHints().hoverSupport === 'events';

const roleProps = (
  elType: string,
  hidden: boolean
): { role?: HTMLAttributesSignalExcluded['role'] } => {
  switch (elType) {
    case 'link':
      return { role: 'link' };
    case 'button':
      return {};
    default:
      return hidden ? { role: 'none' } : { role: 'button' };
  }
};

export const BaseButton = forwardRef(
  (
    {
      variant = 'outlined',
      isDisabled = false,
      isRepeat = false,
      size = 'md',
      elementDetails = { type: 'button' },
      styling = ['default'],
      edge = 'none',
      'aria-label': accessibleLabel,
      'aria-hidden': ariaHidden,
      'aria-expanded': ariaExpanded,
      'aria-roledescription': ariaRoledescription,
      'aria-haspopup': ariaHasPopup,
      'aria-describedby': ariaDescribedBy,
      onBlur,
      onFocus,
      onMouseEnter,
      onMouseLeave,
      onTouchEnd,
      onTouchStart,
      ...props
    }: Props,
    ref?:
      | ForwardedRef<HTMLButtonElement>
      | ForwardedRef<HTMLAnchorElement>
      | ForwardedRef<HTMLSpanElement>
  ) => {
    const pressHandler = (e: Event) => {
      if (props.onAction) {
        props.onAction({
          reason: e.type == 'keyup' ? 'keyboard' : 'pointer'
        });
      }
    };

    const { pressProps } = usePress(pressHandler, {
      isDisabled,
      isRepeat
    });
    const isStyled = (name: StylingOptions) => {
      return styling.indexOf(name) > -1;
    };
    const { hoverProps, isHover } = useHover({ isDisabled: !isHybrid });
    const { activeProps, isActive } = useActive();
    const { class: cls, ...styles } = styleInterpolations(props);
    const { type: elementType = 'button', ...elementProps } = { ...elementDetails };
    const role = roleProps(elementType, ariaHidden ?? false);
    const ElementType = elementDetails.type;
    const isSpan = ElementType == 'span';
    const isBaseButton = ElementType == 'button';
    const { isTabbable } = useTabbableMode();
    const rootStyles = classNames([
      !isStyled('unstyled') && compStyles.base,
      isStyled('unstyled') && compStyles.unstyled,
      isStyled('embedded') && compStyles.embedded,
      isStyled('container') && compStyles.container,
      isStyled('noBorderRadiusStart') && compStyles.noBorderRadiusStart,
      isStyled('noBorderRadiusEnd') && compStyles.noBorderRadiusEnd,
      isStyled('active') && compStyles.active,
      isStyled('fill') && compStyles.fill,
      isStyled('min') && compStyles.min,
      `oj-c-base-button-${variant}`,
      `oj-c-base-button-${size}`,
      isDisabled && compStyles.disabled,
      edge === 'bottom' && compStyles.bottom,
      !isHybrid && !isSpan && !isActive && compStyles.pseudohover,
      !isHybrid &&
        isSpan &&
        !isActive &&
        !isDisabled &&
        !isStyled('container') &&
        compStyles.pseudohoverSpan,
      isHybrid && isHover && !isActive && !isDisabled && !isStyled('container') && compStyles.hover,
      !isStyled('container') && isActive && !isDisabled && compStyles.active,
      childrenStyles
    ]);

    const globalEventProps = {
      onBlur,
      onFocus,
      onMouseEnter,
      onMouseLeave,
      onTouchEnd,
      onTouchStart
    };

    const mergedProps = mergeProps(
      elementProps,
      pressProps,
      iosProps,
      hoverProps,
      isStyled('container') ? {} : activeProps,
      isBaseButton ? {} : { 'aria-disabled': isDisabled },
      globalEventProps
    );

    const wrapperClasses = classNames([cls, rootStyles]);

    return (
      <ElementType
        // The ref casting was done to satisfy TS compiler, when the forwardRef had to be updated to ForwardedRef.
        ref={
          ref as ForwardedRef<HTMLButtonElement> &
            ForwardedRef<HTMLAnchorElement> &
            ForwardedRef<HTMLSpanElement>
        }
        disabled={isDisabled}
        class={wrapperClasses}
        style={styles}
        autofocus={props['autofocus']}
        title={props.title}
        tabIndex={isDisabled || !isTabbable ? -1 : 0}
        aria-label={accessibleLabel}
        aria-haspopup={ariaHasPopup ? 'menu' : undefined}
        aria-describedby={ariaDescribedBy}
        aria-hidden={ariaHidden}
        aria-roledescription={ariaRoledescription}
        aria-expanded={ariaExpanded}
        {...mergedProps}
        {...role}>
        {props.children}
      </ElementType>
    );
  }
);
